import {
  useQuery as useProductPromotionsQuery,
  useInvalidateQuery as useInvalidatePromotionsQuery,
} from "@faire/web-api/api/product/productToken/product-promo-messages/get-hooks";
import { IInformationMessage } from "@faire/web-api/indigofair/data/IInformationMessage";
import { useMemo } from "react";

import { useSettingProductPageReactQuery } from "@faire/retailer/settings/useSettingProductPageReactQuery";

export type ProductPromotionsStateHooks = ReturnType<
  typeof useProductPromotionsState
>;

export const useProductPromotionsState = (
  productToken: string | undefined,
  fetchProductPromotions: boolean | undefined
) => {
  const productPageReactQuerySetting = useSettingProductPageReactQuery();
  const invalidatePromotionsQuery = useInvalidatePromotionsQuery();
  const { data: productPromotions } = useProductPromotionsQuery(
    productToken ?? "",
    {
      refetchOnMount: false,
      enabled:
        productPageReactQuerySetting &&
        !!productToken &&
        fetchProductPromotions,
    }
  );

  const nonFashionWeekPromos = useMemo(
    () =>
      productPromotions?.promo_messages?.filter(
        (promotion) =>
          promotion.type !== IInformationMessage.Type.FAIRE_FUNDED_PROMO_BANNER
      ),
    [productPromotions]
  );
  const marketPromo = useMemo(
    () =>
      productPromotions?.promo_messages?.find(
        (promotion) => promotion.type === IInformationMessage.Type.MARKET_PROMO
      ),
    [productPromotions]
  );
  const promotionStyleBanners = useMemo(
    () =>
      nonFashionWeekPromos?.filter(
        (promotion) => promotion.style === IInformationMessage.Style.PROMO
      ),
    [nonFashionWeekPromos]
  );
  const nonPromotionStyleBanners = useMemo(
    () =>
      nonFashionWeekPromos?.filter(
        (promotion) => promotion.style !== IInformationMessage.Style.PROMO
      ),
    [nonFashionWeekPromos]
  );
  const guaranteedFreeShippingPromo = useMemo(
    () =>
      productPromotions?.promo_messages?.find(
        (promotion) =>
          promotion.type ===
          IInformationMessage.Type.GUARANTEED_FREE_SHIPPING_THRESHOLD
      ),
    [productPromotions]
  );
  return useMemo(
    () => ({
      productPromotions: productPromotions?.promo_messages ?? [],
      insiderLabel: productPromotions?.insider_label,
      promotionLabel: productPromotions?.promo_label,
      nonFashionWeekPromos: nonFashionWeekPromos ?? [],
      marketPromo: marketPromo,
      promotionStyleBanners: promotionStyleBanners ?? [],
      nonPromotionStyleBanners: nonPromotionStyleBanners ?? [],
      guaranteedFreeShippingPromo,
      productToken,
      invalidatePromotionsQuery,
    }),
    [
      productPromotions,
      nonFashionWeekPromos,
      marketPromo,
      promotionStyleBanners,
      nonPromotionStyleBanners,
      guaranteedFreeShippingPromo,
      productToken,
      invalidatePromotionsQuery,
    ]
  );
};
